import React, { useState } from "react";

import { Button, InfoMessage } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import SubscriptionService from "@/services/SubscriptionService";

import { useAuth } from "@/hooks/useAuth";
import { openUpgrade } from "@/hooks/usePlanGate";
import { daysLeft, isTrialing, useSubscription } from "@/hooks/useSubscription";

import { DISMISSED_BANNER_PREFIX } from "@/utils/UUserStorage";

import { PLAN_NAMES } from "@/constants/CPlans";

import { TUser } from "@/types";
import { TPlanCode, TSubscription } from "@/types/TSubscription";

import { promiseToast } from "@/modules/index";

export type TBannerKind =
	| "unpaid"
	| "pastDue"
	| "trialLastDay"
	| "trialEndingSoon"
	| "cancelScheduled"
	| "trialing"
	| "expired";

/**
 * The i18n context of a banner's text: the variant that names a feature
 * (the sharing, the invoices), only while it is on for the user. Paused,
 * the plain text says nothing about it.
 */
export const bannerContext = (
	kind: TBannerKind,
	features: TUser["features"]
): string | undefined => {
	if (kind === "trialEndingSoon" && features?.sharing) return "sharing";
	if (kind === "trialLastDay" && features?.invoices) return "invoices";
	return undefined;
};

export type TBanner = {
	kind: TBannerKind;
	color: "danger" | "warning" | "info";
	/** Where the CTA goes. */
	action: "portal" | "upgrade" | "plans";
	/** Set when the banner may be closed; the key it is remembered under. */
	dismissKey: string | null;
	/** What the copy needs. */
	days: number;
	date: string | null;
};


const formatDate = (iso: string | null) =>
	iso ? format(new Date(iso), "dd/MM/yyyy") : null;

/**
 * The one banner to show, first true wins: broken things before running
 * out, running out before information. NULL when nothing needs saying —
 * free without a history, active, enterprise.
 */
export const bannerFor = (
	subscription: TSubscription | null,
	planCode: TPlanCode,
	now = new Date()
): TBanner | null => {
	if (!subscription) return null;

	const { status, trialEndsAt, currentPeriodEnd, cancelAtPeriodEnd } =
		subscription;
	const base = { days: 0, date: null as string | null };

	if (
		status === "unpaid" ||
		status === "incomplete" ||
		status === "incomplete_expired"
	) {
		return {
			...base,
			kind: "unpaid",
			color: "danger",
			action: "portal",
			dismissKey: null,
		};
	}

	if (status === "past_due") {
		return {
			...base,
			kind: "pastDue",
			color: "warning",
			action: "portal",
			dismissKey: null,
		};
	}

	if (isTrialing(subscription)) {
		const days = daysLeft(trialEndsAt, now);
		if (days <= 1) {
			return {
				...base,
				days,
				kind: "trialLastDay",
				color: "danger",
				action: "upgrade",
				dismissKey: null,
			};
		}
		if (days <= 7) {
			return {
				...base,
				days,
				kind: "trialEndingSoon",
				color: "warning",
				action: "upgrade",
				dismissKey: null,
			};
		}
		return {
			...base,
			days,
			kind: "trialing",
			color: "info",
			action: "plans",
			dismissKey: `trial:${trialEndsAt}`,
		};
	}

	if (status === "active" && cancelAtPeriodEnd) {
		return {
			...base,
			date: formatDate(currentPeriodEnd),
			kind: "cancelScheduled",
			color: "warning",
			action: "portal",
			dismissKey: `cancel:${currentPeriodEnd}`,
		};
	}

	// A trial that ran out, or a paid plan that ended: back on free, the
	// first sessions after deserve one line on why the buttons locked.
	if (
		planCode === "free" &&
		(status === "expired" || status === "canceled")
	) {
		return {
			...base,
			kind: "expired",
			color: "warning",
			action: "plans",
			dismissKey: `expired:${trialEndsAt ?? currentPeriodEnd ?? status}`,
		};
	}

	return null;
};

const isDismissed = (key: string | null): boolean => {
	if (!key) return false;
	try {
		return localStorage.getItem(DISMISSED_BANNER_PREFIX + key) === "1";
	} catch {
		return false;
	}
};

const remember = (key: string) => {
	try {
		localStorage.setItem(DISMISSED_BANNER_PREFIX + key, "1");
	} catch {
		// Private window: it comes back next session, no harm.
	}
};

/**
 * One line above the page while the subscription needs a decision. The
 * free plan without a history says nothing here: it speaks through the
 * locked buttons.
 */
export const SubscriptionBanner: React.FC = () => {
	const { subscription, plan } = useSubscription();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [dismissed, setDismissed] = useState<string | null>(null);

	const banner = bannerFor(subscription, plan?.code ?? "free");
	if (
		!banner ||
		(banner.dismissKey &&
			(dismissed === banner.dismissKey || isDismissed(banner.dismissKey)))
	) {
		return null;
	}

	const act = () => {
		if (banner.action === "portal") {
			const prom = SubscriptionService.portal().then((url) => {
				window.location.assign(url);
			});
			promiseToast(prom, { error: t("subscription.errors.portal") });
		} else if (banner.action === "upgrade") {
			// Keep the plan being tried: the modal opens on it.
			openUpgrade({
				limit: "trial",
				plan: plan?.code ?? "free",
				max: null,
				used: 0,
				requiredPlan: subscription?.plan ?? "team_plus",
			});
		} else {
			navigate("/subscription");
		}
	};

	const dismiss = () => {
		if (!banner.dismissKey) return;
		remember(banner.dismissKey);
		setDismissed(banner.dismissKey);
	};

	const planName = subscription ? PLAN_NAMES[subscription.plan] : "";

	return (
		<InfoMessage
			color={banner.color}
			size="sm"
			className="mx-4 mt-4"
			onClose={banner.dismissKey ? dismiss : undefined}
			data-testid={`subscription-banner-${banner.kind}`}
		>
			<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
				<span>
					{t(`subscription.banner.${banner.kind}`, {
						count: banner.days,
						date: banner.date,
						plan: planName,
						context: bannerContext(banner.kind, user?.features),
					})}
				</span>
				<Button
					color={banner.color}
					variant="link"
					size="sm"
					onClick={act}
				>
					{t(`subscription.banner.${banner.kind}Cta`)}
				</Button>
			</span>
		</InfoMessage>
	);
};
